import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth';
import type { ContentCategory, Paginated, Snapshot } from '../types';
import Pagination from './Pagination';

const PER_PAGE = 50;

type SortKey = 'recent' | 'url' | 'type' | 'size';
type SortDir = 'asc' | 'desc';
// 'substring' = LIKE %q% (find a URL anywhere); 'prefix' = path-prefix match,
// scheme-agnostic, like navigating keys in the S3 console.
type SearchMode = 'substring' | 'prefix';

// Default direction per column — single-click should feel natural:
// recent newest-first, size largest-first, url A→Z.
const DEFAULT_DIR: Record<SortKey, SortDir> = {
  recent: 'desc',
  size: 'desc',
  url: 'asc',
  type: 'asc',
};

/**
 * Self-contained snapshots browser: search + sort + paginated table.
 *
 * Used in two places:
 *   - ArchiveView's snapshots tab (no exactPath — all of the archive)
 *   - ArchiveTreePage's file-mode (exactPath set — one path's variants only)
 *
 * Manages its own page/sort/q state; the only dynamic prop is `exactPath`,
 * which resets the page to 1 when it changes (different path → fresh start).
 */
export default function SnapshotsTable({
  archiveId, exactPath,
}: {
  archiveId: number;
  exactPath?: string;
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState<Paginated<Snapshot> | null>(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortKey>('recent');
  const [dir, setDir] = useState<SortDir>(DEFAULT_DIR.recent);
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [modeInput, setModeInput] = useState<SearchMode>('prefix');
  const [mode, setMode] = useState<SearchMode>('prefix');
  // File-type filter. The dropdown is populated from the categories actually
  // present in this archive; '' = all types. Applies immediately on change
  // (unlike the URL search, which is explicit) since it's a cheap pick.
  const [typeFilter, setTypeFilter] = useState('');
  const [categories, setCategories] = useState<ContentCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  // Multi-select for admin bulk delete. Keyed by snapshot id; cleared
  // whenever the visible page changes (selection is per-page — ids on
  // other pages aren't loaded).
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Search is explicit (button / Enter), not debounced-on-keystroke: the
  // backend scan is slow on big archives, so we only fire on demand. The
  // mode radio is committed alongside the text so toggling it alone doesn't
  // hit the endpoint until the user actually searches.
  function submitSearch() {
    setQ(qInput.trim());
    setMode(modeInput);
    setPage(1);
  }

  // Click a header to sort by it (default direction); click again to flip.
  function toggleSort(key: SortKey) {
    if (sort === key) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(key);
      setDir(DEFAULT_DIR[key]);
    }
    setPage(1);
  }

  function sortIndicator(key: SortKey): string {
    if (sort !== key) return '';
    return dir === 'asc' ? ' ↑' : ' ↓';
  }

  // Reset to page 1 when scope changes (different exactPath = different list).
  useEffect(() => { setPage(1); }, [exactPath]);

  // Load the file-type categories present in this archive for the dropdown.
  useEffect(() => {
    let cancelled = false;
    api<{ data: ContentCategory[] }>(`/archives/${archiveId}/content-categories`)
      .then((r) => { if (!cancelled) setCategories(r.data); })
      .catch(() => { if (!cancelled) setCategories([]); });
    return () => { cancelled = true; };
  }, [archiveId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api<Paginated<Snapshot>>(`/archives/${archiveId}/snapshots`, {
      query: {
        page,
        per_page: PER_PAGE,
        sort,
        dir,
        q: q || undefined,
        mode: q ? mode : undefined,
        type: typeFilter || undefined,
        exact_path: exactPath || undefined,
      },
    })
      .then((res) => { if (!cancelled) { setData(res); setSelected(new Set()); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [archiveId, page, sort, dir, q, mode, typeFilter, exactPath]);

  function toggleOne(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => {
      const rows = data?.data ?? [];
      // If everything on this page is already selected, clear; else select all.
      if (rows.length > 0 && rows.every((r) => prev.has(r.id))) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  }

  async function deleteSelected() {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(
      `Delete ${ids.length} selected snapshot${ids.length === 1 ? '' : 's'}?\n\n`
      + 'Their stored bytes are purged from R2 (unless shared with another '
      + 'snapshot). The URLs stay in the queue as \'done\' — re-add via "Add URL '
      + 'to queue" to re-fetch later. This cannot be undone.'
    )) return;
    setBulkDeleting(true);
    try {
      await api('/snapshots/bulk', { method: 'DELETE', body: { ids } });
      setData((d) => d ? {
        ...d,
        data: d.data.filter((x) => !selected.has(x.id)),
        total: Math.max(0, d.total - ids.length),
      } : d);
      setSelected(new Set());
    } finally {
      setBulkDeleting(false);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input
          type="search"
          placeholder={modeInput === 'prefix' ? 'URL or /path prefix…' : 'Filter URL…'}
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(); }}
          style={{ width: 240 }}
        />
        <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
          {(['substring', 'prefix'] as const).map((m) => (
            <label key={m}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, margin: 0, cursor: 'pointer' }}>
              <input
                type="radio"
                name={`searchMode-${archiveId}`}
                checked={modeInput === m}
                onChange={() => setModeInput(m)}
              />
              {m === 'substring' ? 'Contains' : 'Prefix'}
            </label>
          ))}
        </span>
        <button onClick={submitSearch} disabled={loading}>Search</button>
        {categories.length > 0 && (
          <select
            aria-label="Filter by file type"
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          >
            <option value="">All types</option>
            {categories.map((c) => (
              <option key={c.key} value={c.key}>{c.label} ({c.count.toLocaleString()})</option>
            ))}
          </select>
        )}
        {loading && <span className="spinner" role="status" aria-label="Searching" />}
        {isAdmin && selected.size > 0 && (
          <button
            onClick={deleteSelected}
            disabled={bulkDeleting}
            style={{ color: '#900', borderColor: '#900' }}
          >
            {bulkDeleting ? 'Deleting…' : `Delete selected (${selected.size})`}
          </button>
        )}
        <span className="muted" style={{ fontSize: 12 }}>
          Click column headers to sort.
        </span>
      </div>

      {!data
        ? <p className="muted">Loading…</p>
        : data.data.length === 0
          ? <p className="muted">{q || typeFilter ? 'No snapshots match the current filter.' : 'No snapshots.'}</p>
          : (
            <>
              <table>
                <thead>
                  <tr>
                    {isAdmin && (
                      <th style={{ width: 28 }}>
                        <input
                          type="checkbox"
                          aria-label="Select all on page"
                          checked={data.data.length > 0 && data.data.every((r) => selected.has(r.id))}
                          onChange={toggleAll}
                        />
                      </th>
                    )}
                    <th onClick={() => toggleSort('recent')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Captured{sortIndicator('recent')}
                    </th>
                    <th onClick={() => toggleSort('url')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}>
                      URL{sortIndicator('url')}
                    </th>
                    <th>Status</th>
                    <th onClick={() => toggleSort('type')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Type{sortIndicator('type')}
                    </th>
                    <th onClick={() => toggleSort('size')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Size{sortIndicator('size')}
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((s) => (
                    <tr key={s.id} style={deleting === s.id ? { opacity: 0.4 } : undefined}>
                      {isAdmin && (
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${s.url}`}
                            checked={selected.has(s.id)}
                            onChange={() => toggleOne(s.id)}
                          />
                        </td>
                      )}
                      <td className="muted">{new Date(s.captured_at).toLocaleString()}</td>
                      <td style={{ wordBreak: 'break-all' }}>{s.url}</td>
                      <td>{s.status_code}</td>
                      <td className="muted">{s.content_type ?? '—'}</td>
                      <td>{s.size_bytes.toLocaleString()}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {/* rel must allow Referer so Sanctum recognizes the
                            request as first-party SPA traffic. noopener alone
                            keeps the target=_blank security guarantee. */}
                        <a target="_blank" rel="noopener"
                          href={`/api/replay/${archiveId}/${encodeURIComponent(s.captured_at)}/${encodeURIComponent(s.url)}`}>
                          view
                        </a>
                        {isAdmin && (
                          <>
                            {' · '}
                            <a
                              href="#"
                              style={{ color: '#900' }}
                              onClick={async (e) => {
                                e.preventDefault();
                                if (!window.confirm(
                                  `Delete this snapshot?\n\n${s.url}\n\nThe URL stays in the queue as 'done' — re-add via "Add URL to queue" to re-fetch later.`
                                )) return;
                                setDeleting(s.id);
                                try {
                                  await api(`/snapshots/${s.id}`, { method: 'DELETE' });
                                  setData((d) => d ? {
                                    ...d,
                                    data: d.data.filter((x) => x.id !== s.id),
                                    total: Math.max(0, d.total - 1),
                                  } : d);
                                } finally {
                                  setDeleting(null);
                                }
                              }}
                            >delete</a>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                page={data.current_page}
                lastPage={data.last_page}
                total={data.total}
                perPage={data.per_page}
                onChange={setPage}
              />
            </>
          )
      }
    </>
  );
}
